/**
 * ChainVault WP - P2P Trading Page (BETA)
 * Wallet-based peer-to-peer crypto trading
 * No registration — wallet address is your identity
 * @package ChainVault
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../context/WalletContext';
import toast from 'react-hot-toast';
import { ethers } from 'ethers';

const API_BASE = ( window.chainvaultSettings?.siteUrl || '' ) + '/wp-json/chainvault/v1';

const COINS    = [ 'ETH', 'SOL', 'BNB', 'USDT', 'USDC', 'BTC', 'MATIC' ];
const NETWORKS = [ 'Ethereum', 'Solana', 'BSC', 'Polygon', 'Base' ];
const PAYMENTS = [ 'Crypto (on-chain)', 'Bank Transfer', 'PayPal', 'Any' ];

/* ── Trade Modal ── */
function TradeModal( { offer, onClose } ) {
    const { address, walletType, connectMetaMask, isConnecting } = useWallet();
    const [ amount,    setAmount    ] = useState( '' );
    const [ isSending, setIsSending ] = useState( false );
    const [ txHash,    setTxHash    ] = useState( null );
    const isBuy    = offer.type === 'buy';
    const isCrypto = offer.payment === 'Crypto (on-chain)';

    const handleTrade = async () => {
        if ( !address ) { toast.error( 'Connect your wallet first.' ); return; }
        if ( !amount || parseFloat( amount ) <= 0 ) { toast.error( 'Enter a valid amount.' ); return; }
        setIsSending( true );
        try {
            if ( isCrypto && offer.coin !== 'SOL' ) {
                const provider = new ethers.BrowserProvider( window.ethereum );
                const signer   = await provider.getSigner();
                const tx       = await signer.sendTransaction( {
                    to:    offer.wallet,
                    value: ethers.parseEther( amount.toString() ),
                } );
                await tx.wait();
                setTxHash( tx.hash );
            } else {
                // For non-crypto payment methods or SOL — show instructions
                setTxHash( 'manual' );
            }
            // Update trade count
            await fetch( `${ API_BASE }/p2p/offers/${ offer.id }`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify( { trades: ( offer.trades || 0 ) + 1 } ),
            } );
            toast.success( '🎉 Trade initiated!' );
        } catch ( err ) {
            toast.error( 'Failed: ' + err.message );
        } finally {
            setIsSending( false );
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }} onClick={ onClose }>
            <div style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', width: '90%', maxWidth: '440px', padding: '28px' }} onClick={ e => e.stopPropagation() }>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#fff' }}>{ isBuy ? '🟢 Sell to Buyer' : '🔴 Buy from Seller' }</h3>
                    <button onClick={ onClose } style={{ background: 'none', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                </div>

                { txHash ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
                        <h3 style={{ color: '#fff', marginBottom: '8px' }}>Trade Initiated!</h3>
                        { txHash === 'manual' ? (
                            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '16px', marginBottom: '16px', textAlign: 'left' }}>
                                <p style={{ color: '#aaa', fontSize: '13px', marginBottom: '8px' }}>Contact the trader to complete payment:</p>
                                <div style={{ fontFamily: 'monospace', fontSize: '12px', color: '#FC72FF', wordBreak: 'break-all' }}>{ offer.wallet }</div>
                                <p style={{ color: '#888', fontSize: '12px', marginTop: '8px' }}>Payment method: { offer.payment }</p>
                            </div>
                        ) : (
                            <a href={ `https://etherscan.io/tx/${ txHash }` } target="_blank" rel="noopener noreferrer" style={{ display: 'block', padding: '12px', borderRadius: '10px', background: '#FC72FF', color: '#fff', fontWeight: '700', textDecoration: 'none', marginBottom: '10px' }}>View Transaction ↗</a>
                        ) }
                        <button onClick={ onClose } style={{ width: '100%', padding: '12px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>Close</button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px' }}><div style={{ fontSize: '11px', color: '#888' }}>Coin</div><div style={{ fontWeight: '700', color: '#fff' }}>{ offer.coin }</div></div>
                            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px' }}><div style={{ fontSize: '11px', color: '#888' }}>Amount</div><div style={{ fontWeight: '700', color: '#fff' }}>{ offer.amount } { offer.coin }</div></div>
                            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px' }}><div style={{ fontSize: '11px', color: '#888' }}>Price</div><div style={{ fontWeight: '700', color: '#fff' }}>{ offer.price }</div></div>
                            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px' }}><div style={{ fontSize: '11px', color: '#888' }}>Payment</div><div style={{ fontWeight: '700', color: '#fff' }}>{ offer.payment }</div></div>
                        </div>

                        { offer.note && <div style={{ background: 'rgba(255,170,51,0.08)', border: '1px solid rgba(255,170,51,0.2)', borderRadius: '8px', padding: '10px', fontSize: '13px', color: '#ffaa33' }}>📝 { offer.note }</div> }

                        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#666' }}>
                            Trader: <span style={{ fontFamily: 'monospace', color: '#888' }}>{ offer.wallet.slice(0,16) }...{ offer.wallet.slice(-6) }</span>
                        </div>

                        { isCrypto && (
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#ccc', marginBottom: '8px' }}>Amount to send ({ offer.coin })</label>
                                <div style={{ display: 'flex', alignItems: 'center', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px' }}>
                                    <input type="number" value={ amount } onChange={ e => setAmount( e.target.value ) } placeholder="0.00" style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: '#fff', fontSize: '20px', fontWeight: '700', fontFamily: 'inherit' }} />
                                    <span style={{ color: '#FC72FF', fontWeight: '700' }}>{ offer.coin }</span>
                                </div>
                            </div>
                        ) }

                        { !address ? (
                            <button onClick={ connectMetaMask } disabled={ isConnecting } style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: '#FC72FF', color: '#fff', fontWeight: '700', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                { isConnecting ? 'Connecting...' : 'Connect Wallet to Trade' }
                            </button>
                        ) : (
                            <button onClick={ handleTrade } disabled={ isSending } style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: '#FC72FF', color: '#fff', fontWeight: '700', fontSize: '15px', cursor: isSending ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: isSending ? 0.7 : 1 }}>
                                { isSending ? '⏳ Processing...' : `Confirm Trade` }
                            </button>
                        ) }
                        <p style={{ fontSize: '11px', color: '#555', textAlign: 'center', margin: 0 }}>Always verify the trader before sending funds. ChainVault is not responsible for disputes.</p>
                    </div>
                ) }
            </div>
        </div>
    );
}

/* ── Post Offer Modal ── */
function PostOfferModal( { onClose, onPosted } ) {
    const { address } = useWallet();
    const [ form, setForm ] = useState( { type: 'sell', coin: 'ETH', amount: '', price: '', payment: 'Crypto (on-chain)', network: 'Ethereum', note: '' } );
    const [ isPosting, setIsPosting ] = useState( false );
    const update = ( k, v ) => setForm( p => ( { ...p, [k]: v } ) );

    const handlePost = async () => {
        if ( !address ) { toast.error( 'Connect your wallet first.' ); return; }
        if ( !form.amount || !form.price ) { toast.error( 'Fill in amount and price.' ); return; }
        setIsPosting( true );
        try {
            const res  = await fetch( `${ API_BASE }/p2p/offers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify( { ...form, wallet: address } ),
            } );
            const data = await res.json();
            if ( data.id ) {
                toast.success( '✅ Offer posted!' );
                onPosted();
                onClose();
            } else {
                toast.error( 'Failed to post offer.' );
            }
        } catch ( err ) {
            toast.error( 'Error: ' + err.message );
        } finally {
            setIsPosting( false );
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(6px)' }} onClick={ onClose }>
            <div style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', width: '90%', maxWidth: '460px', padding: '28px', maxHeight: '90vh', overflowY: 'auto' }} onClick={ e => e.stopPropagation() }>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#fff' }}>Post Trade Offer</h3>
                    <button onClick={ onClose } style={{ background: 'none', border: 'none', color: '#888', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    { /* Buy/Sell toggle */ }
                    <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '4px' }}>
                        { [ 'buy', 'sell' ].map( t => (
                            <button key={ t } onClick={ () => update( 'type', t ) } style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: form.type === t ? ( t === 'buy' ? '#00e5a0' : '#ff5577' ) : 'transparent', color: form.type === t ? ( t === 'buy' ? '#000' : '#fff' ) : '#888', fontWeight: '700', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase' }}>
                                { t === 'buy' ? '🟢 I Want to Buy' : '🔴 I Want to Sell' }
                            </button>
                        ) ) }
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase' }}>Coin</label>
                            <select value={ form.coin } onChange={ e => update( 'coin', e.target.value ) } style={{ width: '100%', padding: '10px', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontFamily: 'inherit' }}>
                                { COINS.map( c => <option key={ c } value={ c }>{ c }</option> ) }
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase' }}>Network</label>
                            <select value={ form.network } onChange={ e => update( 'network', e.target.value ) } style={{ width: '100%', padding: '10px', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontFamily: 'inherit' }}>
                                { NETWORKS.map( n => <option key={ n } value={ n }>{ n }</option> ) }
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase' }}>Amount ({ form.coin })</label>
                            <input type="number" value={ form.amount } onChange={ e => update( 'amount', e.target.value ) } placeholder="0.00" style={{ width: '100%', padding: '10px', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase' }}>Price (e.g. $2,500)</label>
                            <input type="text" value={ form.price } onChange={ e => update( 'price', e.target.value ) } placeholder="e.g. $2,500" style={{ width: '100%', padding: '10px', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                        </div>
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase' }}>Payment Method</label>
                        <select value={ form.payment } onChange={ e => update( 'payment', e.target.value ) } style={{ width: '100%', padding: '10px', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontFamily: 'inherit' }}>
                            { PAYMENTS.map( p => <option key={ p } value={ p }>{ p }</option> ) }
                        </select>
                    </div>

                    <div>
                        <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase' }}>Note (optional)</label>
                        <textarea value={ form.note } onChange={ e => update( 'note', e.target.value ) } placeholder="e.g. Fast trader, preferred payment bank transfer only..." style={{ width: '100%', padding: '10px', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontFamily: 'inherit', height: '70px', resize: 'none', boxSizing: 'border-box' }} />
                    </div>

                    { address && (
                        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '10px', fontSize: '11px', color: '#666' }}>
                            Your wallet: <span style={{ fontFamily: 'monospace', color: '#888' }}>{ address.slice(0,16) }...{ address.slice(-6) }</span>
                        </div>
                    ) }

                    { !address ? (
                        <div style={{ background: 'rgba(255,170,51,0.1)', border: '1px solid rgba(255,170,51,0.3)', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#ffaa33', textAlign: 'center' }}>⚠️ Connect your wallet to post offers</div>
                    ) : (
                        <button onClick={ handlePost } disabled={ isPosting } style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: form.type === 'buy' ? '#00e5a0' : '#FC72FF', color: form.type === 'buy' ? '#000' : '#fff', fontWeight: '700', fontSize: '15px', cursor: isPosting ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: isPosting ? 0.7 : 1 }}>
                            { isPosting ? '⏳ Posting...' : `Post ${ form.type === 'buy' ? 'Buy' : 'Sell' } Offer` }
                        </button>
                    ) }
                </div>
            </div>
        </div>
    );
}

/* ── Offer Card ── */
function OfferCard( { offer, currentWallet, onTrade, onDelete } ) {
    const isBuy    = offer.type === 'buy';
    const isOwn    = currentWallet && offer.wallet.toLowerCase() === currentWallet.toLowerCase();
    const timeAgo  = () => {
        const diff = Math.floor( ( Date.now() / 1000 ) - offer.created_at );
        if ( diff < 60  ) return diff + 's ago';
        if ( diff < 3600 ) return Math.floor( diff / 60 ) + 'm ago';
        if ( diff < 86400 ) return Math.floor( diff / 3600 ) + 'h ago';
        return Math.floor( diff / 86400 ) + 'd ago';
    };

    return (
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid ' + ( isBuy ? 'rgba(0,229,160,0.15)' : 'rgba(255,85,119,0.15)' ), borderRadius: '14px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            { /* Header */ }
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', background: isBuy ? 'rgba(0,229,160,0.12)' : 'rgba(255,85,119,0.12)', color: isBuy ? '#00e5a0' : '#ff5577', border: '1px solid ' + ( isBuy ? 'rgba(0,229,160,0.3)' : 'rgba(255,85,119,0.3)' ) }}>
                        { isBuy ? '🟢 BUY' : '🔴 SELL' }
                    </span>
                    <span style={{ fontWeight: '800', fontSize: '16px', color: '#fff' }}>{ offer.coin }</span>
                    { isOwn && <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', background: 'rgba(252,114,255,0.15)', color: '#FC72FF' }}>Your offer</span> }
                </div>
                <span style={{ fontSize: '11px', color: '#666' }}>{ timeAgo() }</span>
            </div>

            { /* Details */ }
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '8px' }}><div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>AMOUNT</div><div style={{ fontWeight: '700', color: '#fff', fontSize: '13px' }}>{ offer.amount } { offer.coin }</div></div>
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '8px' }}><div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>PRICE</div><div style={{ fontWeight: '700', color: '#fff', fontSize: '13px' }}>{ offer.price }</div></div>
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '8px', padding: '8px' }}><div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>NETWORK</div><div style={{ fontWeight: '700', color: '#fff', fontSize: '13px' }}>{ offer.network }</div></div>
            </div>

            { /* Payment + Trader */ }
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                <span style={{ color: '#aaa' }}>💳 { offer.payment }</span>
                <span style={{ color: '#666', fontFamily: 'monospace' }}>{ offer.wallet.slice(0,10) }...{ offer.wallet.slice(-4) }</span>
            </div>

            { offer.note && <div style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '8px' }}>📝 { offer.note }</div> }

            { /* Actions */ }
            { isOwn ? (
                <button onClick={ () => onDelete( offer ) } style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid rgba(255,85,119,0.3)', background: 'rgba(255,85,119,0.08)', color: '#ff5577', fontWeight: '600', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>
                    🗑 Remove My Offer
                </button>
            ) : (
                <button onClick={ () => onTrade( offer ) } style={{ width: '100%', padding: '12px', borderRadius: '10px', border: 'none', background: isBuy ? '#00e5a0' : '#FC72FF', color: isBuy ? '#000' : '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>
                    { isBuy ? '💰 Sell to this Buyer' : '🛒 Buy from this Seller' }
                </button>
            ) }
        </div>
    );
}

/* ── Main P2P Page ── */
function P2PPage() {
    const { address, connectMetaMask, isConnecting } = useWallet();
    const [ offers,       setOffers       ] = useState( [] );
    const [ loading,      setLoading      ] = useState( true );
    const [ activeTab,    setActiveTab    ] = useState( 'all' );
    const [ filterCoin,   setFilterCoin   ] = useState( 'all' );
    const [ showPost,     setShowPost     ] = useState( false );
    const [ selectedOffer,setSelectedOffer] = useState( null );

    const loadOffers = useCallback( async () => {
        setLoading( true );
        try {
            const res  = await fetch( `${ API_BASE }/p2p/offers` );
            const data = await res.json();
            setOffers( Array.isArray( data ) ? data : [] );
        } catch ( err ) {
            console.error( 'P2P load error:', err );
            setOffers( [] );
        } finally {
            setLoading( false );
        }
    }, [] );

    useEffect( () => { loadOffers(); }, [ loadOffers ] );

    const handleDelete = async ( offer ) => {
        if ( !address ) return;
        try {
            await fetch( `${ API_BASE }/p2p/offers/${ offer.id }?wallet=${ encodeURIComponent( address ) }`, { method: 'DELETE' } );
            toast.success( 'Offer removed!' );
            loadOffers();
        } catch ( err ) {
            toast.error( 'Failed to remove offer.' );
        }
    };

    const filtered = offers.filter( o => {
        if ( activeTab !== 'all' && o.type !== activeTab ) return false;
        if ( filterCoin !== 'all' && o.coin !== filterCoin ) return false;
        return true;
    } );

    const buyCount  = offers.filter( o => o.type === 'buy'  ).length;
    const sellCount = offers.filter( o => o.type === 'sell' ).length;

    return (
        <div className="cv-page">
            { /* Header */ }
            <div className="cv-launchpad-header">
                <h1>🤝 P2P Trading <span style={{ fontSize: '14px', fontWeight: '600', padding: '4px 12px', borderRadius: '20px', background: 'rgba(255,170,51,0.15)', color: '#ffaa33', border: '1px solid rgba(255,170,51,0.3)', marginLeft: '8px', verticalAlign: 'middle' }}>BETA</span></h1>
                <p>Trade crypto peer-to-peer. Connect your wallet — no registration needed.</p>
            </div>

            { /* Stats */ }
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '24px' }}>
                { [
                    { label: 'Active Offers', value: offers.length, color: '#FC72FF' },
                    { label: 'Buy Orders',    value: buyCount,       color: '#00e5a0' },
                    { label: 'Sell Orders',   value: sellCount,      color: '#ff5577' },
                ].map( ( s, i ) => (
                    <div key={ i } style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{ s.label }</div>
                        <div style={{ fontSize: '24px', fontWeight: '800', color: s.color }}>{ s.value }</div>
                    </div>
                ) ) }
            </div>

            { /* Controls */ }
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                { /* Type filter */ }
                <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '4px', gap: '2px' }}>
                    { [ ['all','All'], ['buy','Buy'], ['sell','Sell'] ].map( ( [val, label] ) => (
                        <button key={ val } onClick={ () => setActiveTab( val ) } style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: activeTab === val ? '#FC72FF' : 'transparent', color: activeTab === val ? '#fff' : '#888', fontWeight: '600', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>{ label }</button>
                    ) ) }
                </div>

                { /* Coin filter */ }
                <select value={ filterCoin } onChange={ e => setFilterCoin( e.target.value ) } style={{ padding: '9px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', color: '#fff', fontFamily: 'inherit', fontSize: '13px' }}>
                    <option value="all">All Coins</option>
                    { COINS.map( c => <option key={ c } value={ c }>{ c }</option> ) }
                </select>

                <div style={{ flex: 1 }} />

                { /* Post offer button */ }
                { !address ? (
                    <button onClick={ connectMetaMask } disabled={ isConnecting } style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: '#FC72FF', color: '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>
                        { isConnecting ? 'Connecting...' : '🔗 Connect Wallet' }
                    </button>
                ) : (
                    <button onClick={ () => setShowPost( true ) } style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: '#FC72FF', color: '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>
                        + Post Offer
                    </button>
                ) }
            </div>

            { /* Beta warning */ }
            <div style={{ background: 'rgba(255,170,51,0.08)', border: '1px solid rgba(255,170,51,0.2)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#ffaa33' }}>
                ⚠️ <strong>BETA:</strong> P2P trading is peer-to-peer. Always verify the trader before sending funds. ChainVault does not hold escrow or mediate disputes. Trade at your own risk.
            </div>

            { /* Offers grid */ }
            { loading ? (
                <div className="cv-market-loading"><div className="cv-market-spinner"></div><p>Loading offers...</p></div>
            ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤝</div>
                    <h3 style={{ color: '#fff', marginBottom: '8px' }}>No offers yet</h3>
                    <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '20px' }}>Be the first to post a trade offer!</p>
                    { address && <button onClick={ () => setShowPost( true ) } style={{ padding: '12px 28px', borderRadius: '12px', border: 'none', background: '#FC72FF', color: '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}>+ Post First Offer</button> }
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                    { filtered.map( offer => (
                        <OfferCard key={ offer.id } offer={ offer } currentWallet={ address } onTrade={ setSelectedOffer } onDelete={ handleDelete } />
                    ) ) }
                </div>
            ) }

            { /* Modals */ }
            { showPost     && <PostOfferModal onClose={ () => setShowPost( false ) } onPosted={ loadOffers } /> }
            { selectedOffer && <TradeModal offer={ selectedOffer } onClose={ () => setSelectedOffer( null ) } /> }
        </div>
    );
}

export default P2PPage;
